import axios from 'axios';
import { createMutation, createQuery } from 'react-query-kit';

import { relativeUrlResolver } from '@/services/utils';

const LABELS_API_BASE = '/api/v2/labels/';

interface AssetLabel {
    id: string;
    name: string;
    color?: string;
    /** Number of assets carrying the label, see `LabelService#countAssignedAssets`. */
    assetCount: number;
}

interface CreateLabelVariables {
    name: string;
    color?: string;
}

interface UpdateLabelVariables {
    id: string;
    name?: string;
    color?: string;
}

const ASSET_LABEL_TAG_PREFIX = 'assetLabel:';

/** `LabelService#createLabel` rejects labels without a colour, and the designs offer no colour picker yet. */
const DEFAULT_LABEL_COLOR = '#6B7280';

/** Search-index tag that marks an asset as carrying the label, see `Tags.ASSET_LABEL`. */
const assetLabelTag = (labelId: string) => `${ASSET_LABEL_TAG_PREFIX}${labelId}`;

const useLabels = createQuery<AssetLabel[], void, Error>({
    queryKey: ['assets', 'labels'],
    fetcher: async () => {
        const response = await axios.get(relativeUrlResolver(LABELS_API_BASE));
        return response.data ?? [];
    },
});

const useCreateLabel = createMutation<AssetLabel, CreateLabelVariables, Error>({
    mutationFn: async ({ name, color = DEFAULT_LABEL_COLOR }) => {
        const response = await axios.post(relativeUrlResolver(LABELS_API_BASE), { name, color });
        return response.data;
    },
});

const useUpdateLabel = createMutation<AssetLabel, UpdateLabelVariables, Error>({
    mutationFn: async ({ id, name, color }) => {
        const response = await axios.put(relativeUrlResolver(`${LABELS_API_BASE}${encodeURIComponent(id)}`), { name, color });
        return response.data;
    },
});

/** Label ids carried by an asset's `tags__`. */
const assetLabelIds = (tags: unknown): string[] => (Array.isArray(tags) ? tags : [])
    .filter((tag): tag is string => typeof tag === 'string' && tag.startsWith(ASSET_LABEL_TAG_PREFIX))
    .map((tag) => tag.slice(ASSET_LABEL_TAG_PREFIX.length));

const assetLabelsUrl = (primaryKey: string, labelId?: string) => relativeUrlResolver(
    `/api/v2/assets/${encodeURIComponent(primaryKey)}/labels${labelId ? `/${encodeURIComponent(labelId)}` : ''}`,
);

interface AssetLabelVariables {
    primaryKey: string;
    labelId: string;
}

const assignLabel = async ({ primaryKey, labelId }: AssetLabelVariables) =>
    (await axios.post(assetLabelsUrl(primaryKey), { labelId })).data;

const removeLabel = async ({ primaryKey, labelId }: AssetLabelVariables) =>
    (await axios.delete(assetLabelsUrl(primaryKey, labelId))).data;

const useAssignLabel = createMutation<unknown, AssetLabelVariables, Error>({ mutationFn: assignLabel });

const useRemoveLabel = createMutation<unknown, AssetLabelVariables, Error>({ mutationFn: removeLabel });

export { assetLabelIds, assetLabelTag, assignLabel, removeLabel, useAssignLabel, useCreateLabel, useLabels, useRemoveLabel, useUpdateLabel };
export type { AssetLabel };
